import { Modal, ModalDialog, Stack, Typography, IconButton } from '@mui/joy';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { useEffect, useState } from 'react';

import GFormProvider from '../../../../technical/form/GFormProvider';
import SubmitButton from '../../../../technical/form/SubmitButton';
import { GraphQLApiFormErrorMessage } from '../../../../technical/form/GraphQLApiErrorMessage';
import gYupResolver from '../../../../technical/form/gYupResolver';
import GDialogHeader from '../../../../technical/GDialog/GDialogHeader';
import GButton from '../../../../technical/inputs/GButton/GButton';
import { defaultRowSpacing } from '../../../../StackSpacing';
import AssetLabel from '../../AssetLabel';
import { Edit } from '@mui/icons-material';

import { useGraphQLApiError } from '../../../../technical/form/UseGraphQLApiError';
import {
  useUpdateUnderlyingAssetMutation,
  useUnderlyingAssetsLazyQuery,
  useDeleteUnderlyingAssetOverrideMutation,
} from '../../../../../generated/graphql';

import {
  createAssetAutocompleteProps,
  type NotVerifiedAssetWithId,
  useAccountAssetPaginatedOptions,
} from '../../AssetService';

import GSingleAutocomplete from 'components/technical/inputs/Autocomplete/GSingleAutocomplete';
import SelectedChipsBoard from 'components/technical/inputs/Autocomplete/SelectedChipsBoard';
import { IconVariant } from '../../cryptocurrencies/CryptocurrenciesData';
import { useFeedback } from 'components/technical/Feedback/UseFeedback';
import { Close } from 'components/technical/icons';

const schema = yup.object({
  underlyingAssetId: yup.string().required(),
  assets: yup.array().of(yup.object()).required().min(1),
});

export type EditUnderlyingModalProps = {
  selected: {
    underlyingAsset: NotVerifiedAssetWithId;
    assets: NotVerifiedAssetWithId[];
  } | null;
  onClose: () => void;
  onSuccess: () => void;
};

export default function EditUnderlyingModal({ onClose, selected, onSuccess }: EditUnderlyingModalProps) {
  const assetOptions = useAccountAssetPaginatedOptions({
    hideDerivatives: true,
  });
  const [fetchOverrides, queryOutput] = useUnderlyingAssetsLazyQuery();
  const [updateOverride] = useUpdateUnderlyingAssetMutation();
  const [deleteOverride] = useDeleteUnderlyingAssetOverrideMutation();
  const { showSuccessMessage } = useFeedback();
  const [underlyingAsset, setUnderlyingAsset] = useState<NotVerifiedAssetWithId | null>(
    selected?.underlyingAsset ?? null
  );

  const methods = useForm<{
    underlyingAssetId: string;
    assets: NotVerifiedAssetWithId[];
  }>({
    resolver: gYupResolver(schema),
    defaultValues: {
      underlyingAssetId: selected?.underlyingAsset?.id ?? '',
      assets: selected?.assets ?? [],
    },
  });

  const { onErrorAndThrow } = useGraphQLApiError(methods);
  const { setValue, getValues } = methods;

  // Reset form when selected changes
  useEffect(() => {
    setUnderlyingAsset(selected?.underlyingAsset ?? null);
    setValue('underlyingAssetId', selected?.underlyingAsset?.id ?? '');
    setValue('assets', selected?.assets ?? []);
  }, [selected, setValue]);

  // Handle query results
  useEffect(() => {
    const overrides = queryOutput?.data?.assets?.underlyingAssetOverrides ?? [];
    if (overrides.length > 0) {
      setValue(
        'assets',
        overrides.map((o) => o.asset)
      );
    }
  }, [queryOutput, setValue]);

  const handleSelectUnderlying = async (asset: NotVerifiedAssetWithId | null) => {
    if (!asset) return;

    setUnderlyingAsset(asset);
    setValue('underlyingAssetId', asset.id);

    const { error } = await fetchOverrides({
      variables: { underlyingAssetId: asset.id },
    });

    if (error) {
      onErrorAndThrow(error);
    }
  };

  const handleAddAsset = (assetToAdd: NotVerifiedAssetWithId | null) => {
    if (!assetToAdd) return;
    const currentAssets = getValues('assets');
    if (currentAssets.some((a: NotVerifiedAssetWithId) => a.id === assetToAdd.id)) return;
    setValue('assets', [...currentAssets, assetToAdd]);
  };

  // Delete asset from underlying asset
  const handleDeleteAsset = async (assetToDelete: NotVerifiedAssetWithId) => {
    try {
      await deleteOverride({
        variables: {
          input: { assetIds: [assetToDelete.id] },
        },
      });
      const currentAssets = getValues('assets');
      setValue(
        'assets',
        currentAssets.filter((a: NotVerifiedAssetWithId) => a.id !== assetToDelete.id)
      );
      showSuccessMessage('Asset removed from underlying asset');
      onSuccess();
      onClose();
    } catch (e) {
      onErrorAndThrow(e);
    }
  };

  const onSubmit = async (data: { underlyingAssetId: string; assets: NotVerifiedAssetWithId[] } | undefined) => {
    if (!data) return;

    const currentAssets = data.assets;
    const currentUnderlyingAssetId = data.underlyingAssetId;
    try {
      await updateOverride({
        variables: {
          input: {
            underlyingAssetId: currentUnderlyingAssetId,
            assetIds: currentAssets.map((a: NotVerifiedAssetWithId) => a.id),
          },
        },
      });

      showSuccessMessage('Underlying asset updated');
      onSuccess();
      onClose();
    } catch (e) {
      onErrorAndThrow(e);
    }
  };

  return (
    <Modal open onClose={onClose}>
      <ModalDialog layout="center" size="lg" minWidth="md">
        <GFormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit)}>
            <Stack spacing={3} direction="column" width="100%">
              <GDialogHeader>Edit Underlying Assets</GDialogHeader>
              <Stack spacing={3} direction="row" width="100%">
                <Stack direction="column" width="100%">
                  <GSingleAutocomplete<NotVerifiedAssetWithId>
                    value={underlyingAsset}
                    label="Search Underlying Asset"
                    placeholder=""
                    width="xl3"
                    showLabelAboveInput
                    menuWidth="xl3"
                    showChipTooltips
                    disabled={!!underlyingAsset} // instead of hiding it
                    {...createAssetAutocompleteProps()}
                    {...assetOptions}
                    onChange={handleSelectUnderlying}
                  />
                  {underlyingAsset && (
                    <Stack mt={2} direction="column" spacing={0}>
                      <Typography level="body-sm">Selected Underlying Asset</Typography>
                      <Stack spacing={1} direction="row" alignItems="center">
                        <AssetLabel asset={underlyingAsset} wrap={false} size={IconVariant.MEDIUM} withSymbol plain />
                        <IconButton sx={{ margin: 0, padding: 0 }} onClick={() => setUnderlyingAsset(null)}>
                          <Close size="sm" sx={{ margin: 0, padding: 0, width: '.5rem', height: '.5rem' }} />
                        </IconButton>
                      </Stack>
                    </Stack>
                  )}
                </Stack>
                <Stack spacing={3} direction="column" width="100%">
                  <GSingleAutocomplete<NotVerifiedAssetWithId>
                    value={null}
                    label="Add Asset"
                    placeholder=""
                    width="fullWidth"
                    showLabelAboveInput
                    menuWidth="fullWidth"
                    showChipTooltips
                    {...createAssetAutocompleteProps()}
                    {...assetOptions}
                    onChange={handleAddAsset}
                  />
                  <SelectedChipsBoard
                    label="Linked Assets"
                    placeholder="Assets"
                    getItemKey={(a) => a.id}
                    renderItem={(a: NotVerifiedAssetWithId) => (
                      <AssetLabel asset={a} size={IconVariant.SMALL} wrap={false} />
                    )}
                    height={300}
                    width="fullWidth"
                    value={getValues('assets')}
                    onDelete={handleDeleteAsset}
                  />
                </Stack>
              </Stack>
            </Stack>
            <Stack direction="row" mt={2} spacing={defaultRowSpacing} justifyContent="flex-end">
              <GraphQLApiFormErrorMessage />
              <GButton width="normal" variant="outlined" onClick={onClose}>
                Cancel
              </GButton>
              <SubmitButton width="normal" startDecorator={<Edit />}>
                Save
              </SubmitButton>
            </Stack>
          </form>
        </GFormProvider>
      </ModalDialog>
    </Modal>
  );
}
